import { useQueries } from "@tanstack/react-query";
import { fetchBanks, fetchDivergence } from "../lib/api";

export default function CentralBanks() {
  const [stancesResult, divergenceResult] = useQueries({
    queries: [
      { queryKey: ["banks"], queryFn: fetchBanks },
      { queryKey: ["divergence"], queryFn: fetchDivergence },
    ],
  });

  const stances = stancesResult.data?.stances ?? [];
  const matrix = divergenceResult.data?.matrix ?? [];
  const loading = stancesResult.isLoading || divergenceResult.isLoading;

  if (loading) return <div className="text-sm text-slate-500">Loading Central Banks…</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="space-y-2">
        <h1 className="text-lg font-semibold">Central Banks</h1>
        <p className="max-w-2xl text-xs text-slate-500">
          Hawk-dove stance per major central bank and pairwise divergence matrix. Market-implied direction is a proxy, not futures pricing. Educational only.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stances.map((s) => (
          <div
            key={s.bank}
            className="rounded-xl border border-surface-2 bg-surface-1 p-4"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{s.bank}</span>
              <span className="text-[10px] text-slate-500">{s.next_meeting ?? "—"}</span>
            </div>
            <div className="mt-2 font-mono text-3xl font-semibold">{s.hawk_score.toFixed(0)}</div>
            <div className="text-xs text-slate-500">hawk-dove score</div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-surface-2">
              <div
                className={`h-1.5 rounded-full ${s.hawk_score >= 0 ? "bg-macro-up" : "bg-macro-down"}`}
                style={{ width: `${Math.abs(s.hawk_score)}%`, marginLeft: s.hawk_score >= 0 ? "50%" : `${50 - Math.abs(s.hawk_score)}%` }}
              />
            </div>
            {s.coverage_gap && (
              <div className="mt-2 text-[10px] text-yellow-500">{s.coverage_gap}</div>
            )}
            <div className="mt-3 space-y-1">
              {Object.entries(s.drivers).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500 capitalize">{key.replace(/_/g, " ")}</span>
                  <span className={`font-mono ${value >= 0 ? "text-macro-up" : "text-macro-down"}`}>
                    {value >= 0 ? "+" : ""}
                    {value.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Divergence matrix</h2>
        {matrix.length === 0 ? (
          <div className="text-sm text-slate-500">Insufficient data to build divergence matrix.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {matrix.map((d) => (
              <div key={`${d.pair[0]}-${d.pair[1]}`} className="rounded-lg border border-surface-2 bg-surface-2 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {d.pair[0]} vs {d.pair[1]}
                  </span>
                  <span className={`font-mono text-sm ${d.value >= 0 ? "text-macro-up" : "text-macro-down"}`}>
                    {d.value >= 0 ? "+" : ""}
                    {d.value.toFixed(0)}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{d.note}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="text-[10px] text-slate-600">
        Source: {stancesResult.data?.source} / {divergenceResult.data?.source} · {stancesResult.data?.updated_at ? new Date(stancesResult.data.updated_at).toLocaleString() : ""}
        {stancesResult.data?.delayed && " · delayed"} · educational
      </div>
    </div>
  );
}
